import {useLocation, useNavigate} from "react-router-dom";
import {
  Alert,
  Anchor,
  Box,
  Center,
  CloseButton,
  Container,
  Group,
  LoadingOverlay,
  Paper,
  Space,
  Title
} from "@mantine/core";
import {
  adjustToTimeZone,
  adjustToTimeZoneSafely,
  getObjectId,
  getRequiredObjectId,
  TtaDisputeReportCriteria,
  useDownloadTtaDisputeReport
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useEffect} from "react";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {
  TtaDisputeReportForm,
  TtaDisputeReportFormValues
} from "../../components/form/TtaDisputeReportForm/TtaDisputeReportForm";
import {IconAlertCircle} from "@tabler/icons-react";
import {useScrollIntoView} from "@mantine/hooks";

type Props = {
  formDefaults?: Partial<TtaDisputeReportFormValues>
}

export const TtaDisputeReportFormPageRoute = () => {
  const {state} = useLocation();

  const formDefaults = state?.formDefaults;

  return (
    <TtaDisputeReportFormPage formDefaults={formDefaults}/>
  );
};

export const TtaDisputeReportFormPage = (props: Props) => {
    const {formDefaults} = props;

    const navigate = useNavigate();
    const {t} = useTranslation(["common"]);
    const {regionId, baseQueryApi} = useApp()

    const {scrollIntoView: scrollErrorIntoView, targetRef: errorTargetRef} = useScrollIntoView<HTMLDivElement>({
      offset: 120,
      duration: 0,
    });

    const {scrollIntoView, targetRef} = useScrollIntoView<HTMLDivElement>({
      offset: 120,
      duration: 0,
    });

    const {
      downloadTtaDisputeReport,
      report,
      error,
      loading
    } = useDownloadTtaDisputeReport({baseQueryApi})

    const onFormSubmit = useCallback((formValues: TtaDisputeReportFormValues) => {
      if (regionId) {
        const criteria: TtaDisputeReportCriteria = {
          regionId: regionId,
          fromDttm: adjustToTimeZone(formValues.fromDate).getTime(),
          toDttm: adjustToTimeZoneSafely(formValues.toDate)?.getTime(),
          operatorId: getRequiredObjectId(formValues.operator),
          rateModelId: getObjectId(formValues.rateModel),
          truckCompanyIds: formValues.truckCompanies?.map(getRequiredObjectId),
        }

        downloadTtaDisputeReport(criteria)
      }
    }, [regionId, downloadTtaDisputeReport])

    useEffect(() => {
      if (report) {
        scrollIntoView({alignment: 'start'})
      }
    }, [report, scrollIntoView]);

    useEffect(() => {
      if (error) {
        scrollErrorIntoView({alignment: 'start'})
      }
    }, [error, scrollErrorIntoView]);

    return (
      <ThemedPage>
        {(classes) => (
          <Container className={classes.container} fluid={true} py="md">
            <Center>
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {t("common.page.report.ttadispute.title", "Dispute Report", {ns: "common"})}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                {error &&
                  <>
                    <Alert ref={errorTargetRef} icon={<IconAlertCircle size="1rem"/>} title="Error" color="red">
                      {error?.data?.detail ?? t("common.error", "Error", {ns: "common"})}
                    </Alert>
                    <Space h="md"/>
                  </>
                }
                {report &&
                  <>
                    <Alert ref={targetRef} icon={<IconAlertCircle size="1rem"/>} title="Success" color="green">
                      <Anchor href={report} target="_blank">
                        {t("common.action.download", "Download")}
                      </Anchor>
                    </Alert>
                    <Space h="md"/>
                  </>
                }
                <Box pos="relative">
                  <LoadingOverlay visible={loading}/>
                  <TtaDisputeReportForm
                    defaultValues={{
                      ...(formDefaults ?? {}),
                    }}
                    options={{
                      regionId: regionId,
                    }}
                    submitting={loading}
                    onSubmit={onFormSubmit}/>
                </Box>
              </Paper>
            </Center>
          </Container>
        )}
      </ThemedPage>
    );
  }
;

